import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Logout, { LogoutMethod } from "../Logout/Logout";
import { Collapse } from "react-bootstrap";

interface SidebarLinkProps {
  to: string;
  icon: string;
  label: string;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, icon, label }) => {
  const navigate = useNavigate();
  return (
    <li className="nav-item mb-2">
      <NavLink
        to={to}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            e.preventDefault();
            navigate(to);
          }
        }}
        onAuxClick={(e) => {
          e.preventDefault();
          navigate(to);
        }}
        className={({ isActive }) =>
          `nav-link text-white d-flex align-items-center rounded ${
            isActive ? "active-sidebar-menu" : "hover-bg"
          }`
        }
        style={{ padding: "0.5rem 0.5rem" }}
      >
        <i className={`bi ${icon} me-2`}></i> {label}
      </NavLink>
    </li>
  );
};

interface Submodule {
  to: string;
  icon: string;
  label: string;
}

interface SubModuleSidebarLinkProps {
  to: string;
  icon: string;
  label: string;
  submodules: Submodule[];
}

const SubModuleSidebarLink: React.FC<SubModuleSidebarLinkProps> = ({
  to,
  icon,
  label,
  submodules,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isStoriesActive =
    submodules.some((s) =>
      pathname.toLowerCase().includes(s.to.toLowerCase())
    ) || pathname.toLowerCase().includes("stories");

  const [isExpanded, setIsExpanded] = useState(isStoriesActive);

  useEffect(() => {
    if (isStoriesActive) {
      setIsExpanded(true);
    }
  }, [pathname, isStoriesActive]);

  const toggle = () => {
    if (!isExpanded && submodules.length > 0) {
      setIsExpanded(true);
      navigate(submodules[0].to);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <li className="nav-item mb-2">
      <div
        onClick={toggle}
        className={`nav-link text-white d-flex align-items-center rounded ${
          isStoriesActive || isExpanded ? "active-sidebar-menu" : "hover-bg"
        }`}
        style={{ padding: "0.5rem 0.5rem", cursor: "pointer" }}
      >
        <i className={`bi ${icon} me-2`}></i>
        <span style={{ flex: 1 }}>{label}</span>
        <i
          className="bi bi-chevron-down chevron-down-custom"
          style={{
            transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.2s ease-in-out",
          }}
        ></i>
      </div>

      <Collapse in={isExpanded}>
        <div className="ps-3">
          {submodules.map((item, index) => (
            <NavLink
              key={index}
              to={item.to}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                  e.preventDefault();
                  navigate(item.to);
                }
              }}
              onAuxClick={(e) => {
                e.preventDefault();
                navigate(item.to);
              }}
              className={({ isActive }) =>
                `nav-link text-white d-flex align-items-center rounded ${
                  isActive ? "sub-mod-bg-primary" : "sub-mod-hover-bg"
                }`
              }
              style={{ padding: "0.5rem 0.5rem", marginTop: 8 }}
            >
              <i className={`bi ${item.icon} me-2`}></i> {item.label}
            </NavLink>
          ))}
        </div>
      </Collapse>
    </li>
  );
};

const Sidebar: React.FC = () => {
  const logoutRef = useRef<LogoutMethod>(null);
  const navRef = useRef<HTMLElement>(null);
  const [scrollMetrics, setScrollMetrics] = useState({
    visible: false,
    thumbHeight: 0,
    thumbTop: 0,
  });

  const updateScrollbar = () => {
    const el = navRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const visible = scrollHeight > clientHeight + 1;
    if (!visible) {
      setScrollMetrics({ visible: false, thumbHeight: 0, thumbTop: 0 });
      return;
    }
    const trackHeight = clientHeight - 8; // matches top/bottom 4px inset
    const thumbHeight = Math.max(24, (clientHeight / scrollHeight) * trackHeight);
    const maxTop = trackHeight - thumbHeight;
    const thumbTop =
      scrollHeight === clientHeight
        ? 0
        : (scrollTop / (scrollHeight - clientHeight)) * maxTop;
    setScrollMetrics({ visible: true, thumbHeight, thumbTop });
  };

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    updateScrollbar();
    const ro = new ResizeObserver(updateScrollbar);
    ro.observe(el);
    // Re-measure when submenu collapse/expand changes content height
    const mo = new MutationObserver(updateScrollbar);
    mo.observe(el, { childList: true, subtree: true, attributes: true });
    window.addEventListener("resize", updateScrollbar);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", updateScrollbar);
    };
  }, []);

  const onThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = navRef.current;
    if (!el) return;
    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const { scrollHeight, clientHeight } = el;
    const trackHeight = clientHeight - 8;
    const thumbHeight = Math.max(24, (clientHeight / scrollHeight) * trackHeight);
    const maxScroll = scrollHeight - clientHeight;
    const maxTop = trackHeight - thumbHeight;

    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientY - startY;
      const scrollDelta = maxTop > 0 ? (delta / maxTop) * maxScroll : 0;
      el.scrollTop = startScrollTop + scrollDelta;
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <div
      className="d-flex flex-column vh-100 bg-dark text-white pb-3 app-sidebar"
      style={{ width: "250px" }}
    >
      <h5
        className="sidebar-header d-flex align-items-center px-3"
        style={{ height: 56 }}
      >
        Madame Sereine
      </h5>

      <div
        className="sidebar-nav-wrap flex-grow-1 d-flex flex-column"
        style={{ minHeight: 0 }}
      >
        <nav
          ref={navRef}
          className="flex-grow-1 px-2 sidebar-nav-scroll"
          style={{ overflowY: "auto", minHeight: 0 }}
          onScroll={updateScrollbar}
        >
          <ul className="nav nav-pills flex-column mb-3">
            <SidebarLink to="/dashboard" icon="bi-grid-1x2" label="Dashboard" />

            <SidebarLink to="/customers" icon="bi-people" label="Customers" />

            <SidebarLink
              to="/transactions"
              icon="bi-receipt-cutoff"
              label="Transactions"
            />

            <SidebarLink
              to="/Categories"
              icon="bi-folder2-open"
              label="Branches"
            />

            <SidebarLink to="/Programs" icon="bi-easel2" label="Programs" />
            <SidebarLink
              to="/MiniRetreat"
              icon="bi-easel2"
              label="Mini Retreat"
            />

            <SidebarLink to="/Podcast" icon="bi-headphones" label="Podcast" />
            <SidebarLink
              to="/MeditationGuides"
              icon="bi-moon-stars"
              label="Meditation Guides"
            />
            <SidebarLink
              to="/Magazines"
              icon="bi-journal-bookmark"
              label="Magazines"
            />
            <SidebarLink to="/Banners" icon="bi-image" label="Banners" />
            <SidebarLink
              to="/Campaign"
              icon="bi-megaphone"
              label="Campaign"
            />
            <SidebarLink
              to="/promo-codes"
              icon="bi-percent"
              label="Promo Codes"
            />
            <SidebarLink
              to="/contact-us"
              icon="bi-person-lines-fill"
              label="Contact Requests"
            />
            <SidebarLink to="/About" icon="bi-info-circle" label="About" />
            <SidebarLink
              to="/version-control"
              icon="bi-phone"
              label="Version Control"
            />
            <SubModuleSidebarLink
              to="Stories"
              icon="bi-images"
              label="Stories"
              submodules={[
                {
                  to: "/Stories/Lara",
                  icon: "bi-file-text",
                  label: "Lara",
                },
                {
                  to: "/Stories/Cécile",
                  icon: "bi-tree",
                  label: "Cécile",
                },
              ]}
            />
          </ul>
        </nav>
        {scrollMetrics.visible && (
          <div className="sidebar-custom-scrollbar" aria-hidden="true">
            <div
              className="sidebar-custom-scrollbar-thumb"
              style={{
                height: scrollMetrics.thumbHeight,
                top: scrollMetrics.thumbTop,
              }}
              onMouseDown={onThumbMouseDown}
            />
          </div>
        )}
      </div>

      <div
        className="logout-btn px-3"
        style={{ cursor: "pointer" }}
        onClick={() => logoutRef.current?.open()}
      >
        <i className="bi bi-box-arrow-left me-2"></i>
        Logout
      </div>

      <Logout ref={logoutRef} />
    </div>
  );
};

export default Sidebar;
